import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {
  maybeGetValueFromConfig,
  setValueInConfig,
} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import AddIcon from 'ui-library/icons/AddIcon';
import DeleteIcon from 'ui-library/icons/DeleteIcon';
import {immutableRemoveAt, immutableReplace} from 'util/ImmutableListUtil';
import {isJSONArray, assertJSONObject, JSONObject, JSONValue} from 'util/Types';

import jsonSpecToInitialValue from '../initial-values/jsonSpecToInitialValue';
import {
  ConfigShapePath,
  FieldUISchemaWithStandardComponent,
  isShapeUISchema,
  ShapeUISchema,
} from '../StepConfigEditorTypes';
import StepConfigEditorRouter from './StepConfigEditorRouter';

export interface ArrayEditorProps {
  config: JSONObject;
  configShape: GetPipelineConfigSpecType<'array'>;
  dataTestAttrs?: Pick<DataTestAttrs, 'key' | 'index'>;
  onChangeConfig: (config: JSONObject, path: ConfigShapePath, newValue: JSONValue) => void;
  elementUISchema?: FieldUISchemaWithStandardComponent | ShapeUISchema;
  disabled?: boolean;
  configShapePath: ConfigShapePath;
  disableAddRemove?: boolean;
  /** Accessible label for the add-element control (e.g. "Add parameter"). */
  addButtonAltText?: string;
  hideDefaultAddButton?: boolean;
}

export default function ArrayEditor({
  config,
  configShape,
  dataTestAttrs = {},
  onChangeConfig,
  disabled,
  configShapePath,
  elementUISchema,
  disableAddRemove,
  addButtonAltText,
  hideDefaultAddButton,
}: ArrayEditorProps): JSX.Element {
  const arrayValue = maybeGetValueFromConfig(config, configShapePath) ?? [];

  assert(isJSONArray(arrayValue), 'arrayValue is not an array');

  const isDeleteDisabled = disableAddRemove || arrayValue.length <= (configShape.minItems ?? 0);
  const isAddDisabled =
    disableAddRemove || (configShape.maxItems != null && arrayValue.length >= configShape.maxItems);

  const handleElementDelete = (index: number): void => {
    const newArray = immutableRemoveAt(arrayValue, index);
    onChangeConfig(config, configShapePath, newArray);
  };

  const handleElementAdd = (): void => {
    const newElement = jsonSpecToInitialValue(configShape.elementSpec, elementUISchema);
    const newArray = [...arrayValue, newElement];
    onChangeConfig(config, configShapePath, newArray);
  };

  const isShapeElement = elementUISchema != null && isShapeUISchema(elementUISchema);

  const props = {
    config,
    configShape,
    dataTestAttrs,
    onChangeConfig,
    disabled,
    configShapePath,
    arrayValue,
  };

  return (
    <>
      {arrayValue.map(
        (element: JSONValue, index: number): JSX.Element => (
          <div
            key={index}
            className={css(styles.arrayItemContainer)}
            {...getTestProps({component: 'ArrayEditorV2', element: 'item', index: index.toString()})}
          >
            <div className={css(styles.arrayItemContainerInner)}>
              {isShapeElement ? (
                <ShapeElement element={element} index={index} elementUISchema={elementUISchema} {...props} />
              ) : (
                <ScalarElement element={element} index={index} elementUISchema={elementUISchema} {...props} />
              )}
            </div>
            {!disabled && !disableAddRemove && (
              <div className={css(styles.arrayItemContainerDeleteButton)}>
                <Tooltip
                  isDisabled={!isDeleteDisabled}
                  text={`Must have at least ${configShape.minItems ?? 0} ${pluralize(
                    configShape.minItems ?? 0,
                    'item',
                    'items'
                  )}`}
                >
                  <DeleteIcon
                    icon="trash-can"
                    disabled={isDeleteDisabled}
                    onClick={() => {
                      handleElementDelete(index);
                    }}
                  />
                </Tooltip>
              </div>
            )}
          </div>
        )
      )}
      {!disabled && !disableAddRemove && !hideDefaultAddButton && (
        <div>
          <Tooltip
            isDisabled={!isAddDisabled}
            text={configShape.maxItems != null ? `Cannot exceed ${configShape.maxItems} items` : ''}
          >
            <AddIcon disabled={isAddDisabled} onClick={handleElementAdd} altText={addButtonAltText} />
          </Tooltip>
        </div>
      )}
    </>
  );
}

interface BaseElementProps {
  element: JSONValue;
  index: number;
  config: JSONObject;
  configShape: GetPipelineConfigSpecType<'array'>;
  dataTestAttrs: Pick<DataTestAttrs, 'key' | 'index'>;
  onChangeConfig: (config: JSONObject, path: ConfigShapePath, newValue: JSONValue) => void;
  disabled?: boolean;
  configShapePath: ConfigShapePath;
  arrayValue: Array<JSONValue>;
}

interface ShapeElementProps extends BaseElementProps {
  elementUISchema: ShapeUISchema;
}

interface ScalarElementProps extends BaseElementProps {
  elementUISchema?: FieldUISchemaWithStandardComponent;
}

/*
 * Render a shape/object element (e.g., {name: 'foo', dataType: 'text'})
 * The Router will auto-generate a minimal ShapeUISchema for it.
 */
function ShapeElement({
  element,
  index,
  config,
  configShape,
  dataTestAttrs,
  onChangeConfig,
  elementUISchema,
  disabled,
  configShapePath,
  arrayValue,
}: ShapeElementProps): JSX.Element {
  assertJSONObject(element);
  return (
    <StepConfigEditorRouter
      config={element}
      configShape={configShape.elementSpec}
      dataTestAttrs={{...dataTestAttrs, index: index.toString()}}
      onChangeConfig={(_, path, newValue) => {
        // Update the specific field within the shape element
        const updatedElement = setValueInConfig(element, path, newValue);
        onChangeConfig(config, configShapePath, immutableReplace(arrayValue, index, updatedElement));
      }}
      uiSchema={elementUISchema}
      disabled={disabled}
    />
  );
}

/*
 * Render a scalar element (e.g., 'string', 42, true) by wrapping in a temporary shape.
 */
function ScalarElement({
  element,
  index,
  config,
  configShape,
  dataTestAttrs,
  onChangeConfig,
  elementUISchema,
  disabled,
  configShapePath,
  arrayValue,
}: ScalarElementProps): JSX.Element {
  return (
    <StepConfigEditorRouter
      config={{element}}
      configShape={{kind: 'shape', fields: {element: configShape.elementSpec}, required: true}}
      dataTestAttrs={{...dataTestAttrs, index: index.toString()}}
      onChangeConfig={(_, __, newValue) => {
        // For scalar elements, newValue is the updated element directly
        onChangeConfig(config, configShapePath, immutableReplace(arrayValue, index, newValue));
      }}
      uiSchema={
        elementUISchema
          ? {
              ...elementUISchema,
              configShapePath: ['element'],
            }
          : {
              configShapePath: ['element'],
            }
      }
      disabled={disabled}
    />
  );
}

const styles = StyleSheet.create({
  arrayItemContainer: {display: 'flex', alignItems: 'flex-start', gap: '8px'},
  arrayItemContainerInner: {flex: 1},
  arrayItemContainerDeleteButton: {marginTop: '8px'},
});
